import { getBackendUrl } from '@/utils/apiUrl';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Search, Grid, List, Camera, Euro, User, ChevronDown, Trash2, Download, Check, Key, Shield, Send, ExternalLink } from 'lucide-react';

const Dashboard = ({ user }) => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ neu: 0, inProgress: 0, completed: 0, invoiced: 0 });
  const navigate = useNavigate();
  
  // Access status for DAT and GDV
  const [accessStatus, setAccessStatus] = useState({
    dat_status: 'not_requested',
    gdv_status: 'not_requested',
    loading: true
  });
  
  // View and filter state
  const [viewMode, setViewMode] = useState('list');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    assignedTo: '',
    orderType: '',
    status: '',
    workStep: '',
    branch: '',
    mediatedBy: '',
    contactSearch: '',
    overdueFilter: false
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Selection state
  const [selectedCases, setSelectedCases] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    fetchCases();
    checkAccessStatus();
  }, []);

  // Check if user has DAT and GDV access
  const checkAccessStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Use new unified endpoint
      const response = await fetch(getBackendUrl() + '/api/integrations/access-status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAccessStatus({
          dat_status: data.dat_status || 'not_requested',
          gdv_status: data.gdv_status || 'not_requested',
          loading: false
        });
      } else {
        setAccessStatus(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('Error checking access status:', error);
      setAccessStatus(prev => ({ ...prev, loading: false }));
    }
  };

  const fetchCases = async () => {
    try {
      const response = await fetch(getBackendUrl() + '/api/cases', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Filter out dummy cases
        const filteredCases = data.filter(c => {
          return c.customer_name !== 'Neuer Fall' || 
                 (c.customer_email && c.customer_email.length > 0) ||
                 (c.insurance_company && c.insurance_company.length > 0) ||
                 (c.total && c.total > 0);
        });
        setCases(filteredCases);
        
        // Calculate stats based on workflow_status
        const newStats = {
          neu: filteredCases.filter(c => !c.workflow_status || c.workflow_status === null).length,
          inProgress: filteredCases.filter(c => c.workflow_status === 'in-bearbeitung').length,
          completed: filteredCases.filter(c => c.workflow_status === 'abgeschlossen').length,
          invoiced: filteredCases.filter(c => c.workflow_status === 'abgerechnet').length
        };
        setStats(newStats);
      }
    } catch (error) {
      toast.error('Fehler beim Laden der Fälle');
    } finally {
      setLoading(false);
    }
  };

  const updateWorkflowStatus = async (caseId, newStatus, e) => {
    e.stopPropagation();
    
    try {
      const response = await fetch(`${getBackendUrl()}/api/cases/${caseId}/workflow-status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ workflow_status: newStatus })
      });

      if (response.ok) {
        toast.success(`Status aktualisiert: ${getStatusLabel(newStatus)}`);
        fetchCases(); // Reload cases and stats
      } else {
        toast.error('Fehler beim Aktualisieren');
      }
    } catch (error) {
      console.error('Error updating workflow status:', error);
      toast.error('Verbindungsfehler');
    }
  };

  const deleteCase = async (caseId, caseName, e) => {
    e.stopPropagation();
    
    if (!window.confirm(`Möchten Sie die Akte "${caseName}" ins Archiv verschieben?`)) {
      return;
    }

    try {
      const response = await fetch(`${getBackendUrl()}/api/cases/${caseId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        toast.success('Akte wurde ins Archiv verschoben');
        fetchCases();
      } else {
        toast.error('Fehler beim Archivieren der Akte');
      }
    } catch (error) {
      console.error('Error deleting case:', error);
      toast.error('Verbindungsfehler beim Archivieren');
    }
  };

  // Massenlöschen - alle ausgewählten Akten archivieren
  const deleteSelectedCases = async () => {
    if (selectedCases.length === 0) return;
    
    const count = selectedCases.length;
    if (!window.confirm(`Möchten Sie ${count} Akte(n) ins Archiv verschieben? Sie werden nach 14 Tagen automatisch gelöscht.`)) {
      return;
    }

    let successCount = 0;
    let errorCount = 0;

    for (const caseId of selectedCases) {
      try {
        const response = await fetch(`${getBackendUrl()}/api/cases/${caseId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        console.error('Error deleting case:', caseId, error);
        errorCount++;
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} Akte(n) ins Archiv verschoben`);
      fetchCases();
      setSelectedCases([]);
      setSelectAll(false);
    }
    
    if (errorCount > 0) {
      toast.error(`${errorCount} Akte(n) konnten nicht archiviert werden`);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'in-bearbeitung': 'In Bearbeitung',
      'abgeschlossen': 'Abgeschlossen',
      'abgerechnet': 'Abgerechnet'
    };
    return labels[status] || status;
  };

  const getWorkflowBadge = (caseItem) => {
    const status = caseItem.workflow_status;
    
    let displayText = 'Neu';
    let bgColor = 'bg-blue-600 hover:bg-blue-700';
    
    if (status === 'in-bearbeitung') {
      displayText = 'In Bearbeitung';
      bgColor = 'bg-yellow-600 hover:bg-yellow-700';
    } else if (status === 'abgeschlossen') {
      displayText = 'Abgeschlossen';
      bgColor = 'bg-green-600 hover:bg-green-700';
    } else if (status === 'abgerechnet') {
      displayText = 'Abgerechnet';
      bgColor = 'bg-purple-600 hover:bg-purple-700';
    }
    
    return (
      <div className="relative group" onClick={(e) => e.stopPropagation()}>
        <button 
          className={`${bgColor} text-white px-4 py-1 rounded text-xs font-medium inline-flex items-center gap-1 transition-colors`}
          data-testid={`workflow-status-${caseItem._id}`}
          onClick={(e) => e.stopPropagation()}
        >
          {displayText}
          <ChevronDown className="w-3 h-3" />
        </button>
        <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded shadow-lg z-10 hidden group-hover:block min-w-[150px]">
          <button
            onClick={(e) => updateWorkflowStatus(caseItem._id, 'in-bearbeitung', e)}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-yellow-50 transition-colors"
          >
            In Bearbeitung
          </button>
          <button
            onClick={(e) => updateWorkflowStatus(caseItem._id, 'abgeschlossen', e)}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-green-50 transition-colors"
          >
            Abgeschlossen
          </button>
          <button
            onClick={(e) => updateWorkflowStatus(caseItem._id, 'abgerechnet', e)}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-purple-50 transition-colors"
          >
            Abgerechnet
          </button>
        </div>
      </div>
    );
  };

  // Einfache Fortschrittsanzeige: 4 Schritte visualisiert
  const getStatusProgress = (workflow_status) => {
    // Bestimme aktuellen Schritt (1-4)
    let currentStep = 1; // Neu
    if (workflow_status === 'in-bearbeitung') currentStep = 2;
    else if (workflow_status === 'abgeschlossen') currentStep = 3;
    else if (workflow_status === 'abgerechnet') currentStep = 4;
    
    const steps = [
      { label: '1', title: 'Neu' },
      { label: '2', title: 'Bearbeitung' },
      { label: '3', title: 'Fertig' },
      { label: '4', title: 'Rechnung' }
    ];
    
    return (
      <div className="flex items-center gap-0.5" title={`Schritt ${currentStep} von 4`}>
        {steps.map((step, idx) => {
          const stepNum = idx + 1;
          const isActive = stepNum === currentStep;
          const isCompleted = stepNum < currentStep;
          
          return (
            <div 
              key={idx}
              className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold transition-all
                ${isActive ? 'bg-blue-600 text-white ring-2 ring-blue-200' : ''}
                ${isCompleted ? 'bg-green-500 text-white' : ''}
                ${!isActive && !isCompleted ? 'bg-gray-200 text-gray-500' : ''}
              `}
              title={step.title}
            >
              {isCompleted ? '✓' : step.label}
            </div>
          );
        })}
      </div>
    );
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCases = cases.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(cases.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedCases([]);
      setSelectAll(false);
    } else {
      setSelectedCases(currentCases.map(c => c._id));
      setSelectAll(true);
    }
  };

  const handleSelectCase = (caseId) => {
    if (selectedCases.includes(caseId)) {
      setSelectedCases(selectedCases.filter(id => id !== caseId));
      setSelectAll(false);
    } else {
      const newSelected = [...selectedCases, caseId];
      setSelectedCases(newSelected);
      if (newSelected.length === currentCases.length) {
        setSelectAll(true);
      }
    }
  };

  const handleExportData = async () => {
    try {
      if (selectedCases.length === 0) {
        toast.error('Bitte wählen Sie mindestens eine Akte aus');
        return;
      }

      toast.info(`Exportiere ${selectedCases.length} Akte(n)...`);
      
      const response = await fetch(`${getBackendUrl()}/api/export/selected-cases`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ case_ids: selectedCases })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `easyclaim24_akten_export_${new Date().toISOString().split('T')[0]}.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success(`${selectedCases.length} Akte(n) erfolgreich exportiert!`);
        setSelectedCases([]);
        setSelectAll(false);
      } else {
        toast.error('Fehler beim Exportieren der Daten');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Verbindungsfehler beim Exportieren');
    }
  };

  return (
    <Layout user={user}>
      <div className="w-full max-w-full">
        {/* Welcome */}
        <div className="mb-6 md:mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Willkommen zurück</h2>
          <p className="text-sm md:text-base text-gray-600 mt-1">Hier ist Ihre Übersicht für heute</p>
        </div>

        {/* Test Access Warning Banner */}
        {!accessStatus.loading && (accessStatus.dat_status !== 'live' || accessStatus.gdv_status !== 'live') && (
          <div className="mb-6 md:mb-8 bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg" data-testid="test-access-warning">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">
                  ⚠️ Derzeit wird noch mit dem Testzugang gearbeitet
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  {accessStatus.dat_status !== 'live' && accessStatus.gdv_status !== 'live' 
                    ? 'DAT und GDV Zugänge sind noch nicht live. Bitte beantragen Sie die Produktiv-Zugänge in den Einstellungen.'
                    : accessStatus.dat_status !== 'live'
                      ? 'DAT-Zugang ist noch nicht live. Die Kalkulation arbeitet im Test-Modus.'
                      : 'GDV-Zugang ist noch nicht live. Versicherungsdaten werden im Test-Modus abgerufen.'}
                </p>
              </div>
              <button
                onClick={() => navigate('/settings')}
                className="flex-shrink-0 px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-medium rounded-md transition-colors"
              >
                Zu Einstellungen
              </button>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8 w-full">
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border" style={{borderColor: '#E1E4E8'}} data-testid="stat-neu">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#DBEAFE'}}>
                <svg className="w-5 h-5 md:w-6 md:h-6" style={{color: '#2563EB'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <div className="text-xs md:text-sm text-gray-600">Neue Fälle</div>
                <div className="text-2xl md:text-3xl font-bold text-gray-900">{stats.neu}</div>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border" style={{borderColor: '#E1E4E8'}} data-testid="stat-in-progress">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#FEF3C7'}}>
                <svg className="w-5 h-5 md:w-6 md:h-6" style={{color: '#D97706'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <div className="text-xs md:text-sm text-gray-600">In Bearbeitung</div>
                <div className="text-2xl md:text-3xl font-bold text-gray-900">{stats.inProgress}</div>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border" style={{borderColor: '#E1E4E8'}} data-testid="stat-completed">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#D1FAE5'}}>
                <svg className="w-5 h-5 md:w-6 md:h-6" style={{color: '#059669'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <div className="text-xs md:text-sm text-gray-600">Abgeschlossen</div>
                <div className="text-2xl md:text-3xl font-bold text-gray-900">{stats.completed}</div>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border" style={{borderColor: '#E1E4E8'}} data-testid="stat-invoiced">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center" style={{backgroundColor: '#EDE9FE'}}>
                <svg className="w-5 h-5 md:w-6 md:h-6" style={{color: '#9333EA'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <div className="text-xs md:text-sm text-gray-600">Abgerechnet</div>
                <div className="text-2xl md:text-3xl font-bold text-gray-900">{stats.invoiced}</div>
              </div>
            </div>
          </div>
        </div>

        {/* API Access Status Card */}
        {!accessStatus.loading && (
          <div className="mb-6 md:mb-8 bg-white rounded-xl shadow-sm border p-6" style={{borderColor: '#E1E4E8'}}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">API-Zugänge</h3>
                <p className="text-sm text-gray-500">Status Ihrer Integrationen</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* GDV Status */}
              <div 
                onClick={() => navigate('/settings')}
                className="flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer hover:shadow-md transition-all"
                style={{
                  borderColor: accessStatus.gdv_status === 'live' ? '#10B981' : accessStatus.gdv_status === 'requested' ? '#F59E0B' : '#EF4444',
                  backgroundColor: accessStatus.gdv_status === 'live' ? '#D1FAE5' : accessStatus.gdv_status === 'requested' ? '#FEF3C7' : '#FEE2E2'
                }}
                data-testid="gdv-status-card"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-white shadow-sm">
                    <span className="text-lg font-bold" style={{color: '#2563EB'}}>GDV</span>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">GDV Zugang</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span 
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                        style={{
                          backgroundColor: accessStatus.gdv_status === 'live' ? '#10B981' : accessStatus.gdv_status === 'requested' ? '#F59E0B' : '#EF4444',
                          color: 'white'
                        }}
                      >
                        {accessStatus.gdv_status === 'live' && '● Live'}
                        {accessStatus.gdv_status === 'requested' && '● Beantragt'}
                        {accessStatus.gdv_status === 'not_requested' && '● Beantragen'}
                      </span>
                    </div>
                  </div>
                </div>
                <ChevronDown className="w-5 h-5 text-gray-400 transform -rotate-90" />
              </div>

              {/* DAT Status */}
              <div 
                onClick={() => navigate('/settings')}
                className="flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer hover:shadow-md transition-all"
                style={{
                  borderColor: accessStatus.dat_status === 'live' ? '#10B981' : accessStatus.dat_status === 'requested' ? '#F59E0B' : '#EF4444',
                  backgroundColor: accessStatus.dat_status === 'live' ? '#D1FAE5' : accessStatus.dat_status === 'requested' ? '#FEF3C7' : '#FEE2E2'
                }}
                data-testid="dat-status-card"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-white shadow-sm">
                    <span className="text-lg font-bold" style={{color: '#F59E0B'}}>DAT</span>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">DAT Zugang</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span 
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                        style={{
                          backgroundColor: accessStatus.dat_status === 'live' ? '#10B981' : accessStatus.dat_status === 'requested' ? '#F59E0B' : '#EF4444',
                          color: 'white'
                        }}
                      >
                        {accessStatus.dat_status === 'live' && '● Live'}
                        {accessStatus.dat_status === 'requested' && '● Beantragt'}
                        {accessStatus.dat_status === 'not_requested' && '● Beantragen'}
                      </span>
                    </div>
                  </div>
                </div>
                <ChevronDown className="w-5 h-5 text-gray-400 transform -rotate-90" />
              </div>
            </div>
            
            <div className="mt-4 text-xs text-gray-500 text-center">
              Klicken Sie auf eine Karte, um zu den Einstellungen zu gelangen
            </div>
          </div>
        )}

        {/* Zugänge beantragen Banner - nur anzeigen wenn Zugänge fehlen */}
        {!accessStatus.loading && (accessStatus.dat_status === 'not_requested' || accessStatus.gdv_status === 'not_requested') && (
          <div className="mb-6 md:mb-8 bg-gradient-to-r from-blue-600 via-blue-500 to-purple-600 rounded-xl p-4 md:p-6 shadow-lg" data-testid="access-request-banner">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <Key className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-bold text-white">Zugänge beantragen</h3>
                  <p className="text-sm text-blue-100">
                    {accessStatus.dat_status === 'not_requested' && accessStatus.gdv_status === 'not_requested'
                      ? 'Schalten Sie DAT-Kalkulation und GDV-Versicherungsdaten frei'
                      : accessStatus.dat_status === 'not_requested'
                        ? 'Schalten Sie die DAT-Kalkulation frei'
                        : 'Schalten Sie den GDV-Zugang frei'}
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                {accessStatus.dat_status === 'not_requested' && (
                  <button
                    onClick={() => navigate('/settings')}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-blue-600 rounded-lg font-medium hover:bg-blue-50 transition-colors shadow-sm"
                    data-testid="request-dat-access"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                    </svg>
                    DAT Zugang beantragen
                  </button>
                )}
                {accessStatus.gdv_status === 'not_requested' && (
                  <button
                    onClick={() => navigate('/settings')}
                    className={`flex items-center justify-center gap-2 px-4 py-2.5 ${
                      accessStatus.dat_status === 'not_requested'
                        ? 'bg-white/10 backdrop-blur-sm text-white border border-white/30 hover:bg-white/20' 
                        : 'bg-white text-blue-600 hover:bg-blue-50 shadow-sm'
                    } rounded-lg font-medium transition-colors`}
                    data-testid="request-gdv-access"
                  >
                    <Shield className="w-5 h-5" />
                    GDV Zugang freischalten
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Filter Toggle Button */}
        <div className="mb-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm font-medium text-gray-700"
            data-testid="toggle-filters-button"
          >
            <Search className="w-4 h-4" />
            {showFilters ? 'Filter ausblenden' : 'Filter einblenden'}
          </button>
        </div>

        {/* Filter Section - Collapsible */}
        {showFilters && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            {/* First Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Suche..."
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  data-testid="search-input"
                />
              </div>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <select
                  value={filters.assignedTo}
                  onChange={(e) => setFilters({...filters, assignedTo: e.target.value})}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm appearance-none"
                >
                  <option value="">Zugewiesen an</option>
                  <option value="user1">Benutzer 1</option>
                  <option value="user2">Benutzer 2</option>
                </select>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="tt.mm.jjjj"
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                />
                <input
                  type="text"
                  placeholder="tt.mm.jjjj"
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
            </div>

            {/* Second Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <select
                value={filters.orderType}
                onChange={(e) => setFilters({...filters, orderType: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">Auftragstyp</option>
                <option value="haftpflicht">Haftpflichtauftrag</option>
                <option value="kasko">Kaskoauftrag</option>
              </select>
              <select
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">Status</option>
                <option value="neu">Neu</option>
                <option value="in-bearbeitung">In Bearbeitung</option>
                <option value="abgeschlossen">Abgeschlossen</option>
              </select>
              <select
                value={filters.workStep}
                onChange={(e) => setFilters({...filters, workStep: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">Arbeitsschritt</option>
                <option value="erstellt">Erstellt</option>
                <option value="in-arbeit">In Arbeit</option>
                <option value="fertig">Fertiggestellt</option>
              </select>
            </div>
          </div>
        )}

        {/* Export and View Controls */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {selectedCases.length > 0 && (
              <span className="text-sm font-medium text-gray-600">
                {selectedCases.length} ausgewählt
              </span>
            )}
            
            {/* Löschen Button */}
            <button
              onClick={deleteSelectedCases}
              disabled={selectedCases.length === 0}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium shadow-sm ${
                selectedCases.length > 0
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              data-testid="delete-selected-button"
            >
              <Trash2 className="w-4 h-4" />
              <span className="hidden sm:inline">
                {selectedCases.length > 0 ? `${selectedCases.length} löschen` : 'Löschen'}
              </span>
            </button>
            
            {/* Export Button */}
            <button
              onClick={handleExportData}
              disabled={selectedCases.length === 0}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium shadow-sm ${
                selectedCases.length > 0
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              data-testid="export-data-button"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">
                {selectedCases.length > 0 ? `${selectedCases.length} exportieren` : 'Daten exportieren'}
              </span>
            </button>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 border rounded transition-colors ${
                viewMode === 'grid' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
              data-testid="grid-view-button"
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 border rounded transition-colors ${
                viewMode === 'list' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
              data-testid="list-view-button"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Cases Table */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-200 border-t-blue-600 mx-auto"></div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {viewMode === 'list' ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          data-testid="select-all-checkbox"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">FOTO</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">KUNDE / AKTENZEICHEN</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">ARBEITSSCHRITT</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">AMTL. KFZ.</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">HERSTELLER</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">WERTE</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">BEARBEITER</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">STATUS</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">AKTIONEN</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {cases.length === 0 ? (
                      <tr>
                        <td colSpan="10" className="px-4 py-12 text-center text-gray-500">
                          <Camera className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                          <p className="text-sm">Keine Akten vorhanden</p>
                          <p className="text-xs mt-1">Erstellen Sie Ihren ersten Fall über "+ Neuer Fall"</p>
                        </td>
                      </tr>
                    ) : (
                      currentCases.map((caseItem, idx) => (
                        <tr 
                          key={caseItem._id || idx}
                          className="hover:bg-gray-50 transition-colors"
                          data-testid={`case-row-${idx}`}
                        >
                          <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedCases.includes(caseItem._id)}
                              onChange={() => handleSelectCase(caseItem._id)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              data-testid={`case-checkbox-${idx}`}
                            />
                          </td>
                          <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/new-case?edit=${caseItem._id}`)}>
                            <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center shadow-sm border border-gray-200">
                              {caseItem.photos && caseItem.photos.length > 0 ? (
                                <img 
                                  src={getBackendUrl() + caseItem.photos[0]}
                                  alt="Fall Vorschau" 
                                  className="w-full h-full object-cover"
                                  data-testid={`case-photo-${caseItem._id}`}
                                />
                              ) : (
                                <Camera className="w-8 h-8 text-gray-400" />
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/new-case?edit=${caseItem._id}`)}>
                            <div className="flex flex-col">
                              <span className="font-semibold text-gray-900">{caseItem.customer_name || 'Unbekannt'}</span>
                              <span className="text-sm text-gray-600">{caseItem.case_number || caseItem._id?.slice(-8)}</span>
                              <div className="flex items-center gap-1 mt-1">
                                <span className="text-xs text-gray-500">Haftpflichtauftrag</span>
                                <Check className="w-3 h-3 text-green-600" />
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            {getWorkflowBadge(caseItem)}
                          </td>
                          <td className="px-4 py-4">
                            <span className="text-sm text-gray-900">{caseItem.license_plate || '-'}</span>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex flex-col">
                              <span className="text-sm font-medium text-gray-900">{caseItem.manufacturer || '-'}</span>
                              <span className="text-xs text-gray-500">{caseItem.model || ''}</span>
                            </div>
                          </td>
                          {/* WERTE (Rechnungswerte) */}
                          <td className="px-4 py-4">
                            <div className="flex flex-col gap-0.5">
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-500">Netto:</span>
                                <span className="text-sm font-medium text-gray-900">
                                  {caseItem.invoice_total_netto ? parseFloat(caseItem.invoice_total_netto).toFixed(2) : '0.00'} €
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-500">Brutto:</span>
                                <span className="text-sm font-semibold text-blue-700">
                                  {caseItem.invoice_total_brutto ? parseFloat(caseItem.invoice_total_brutto).toFixed(2) : '0.00'} €
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                              <User className="w-4 h-4 text-gray-600" />
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            {getStatusProgress(caseItem.workflow_status)}
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={(e) => deleteCase(caseItem._id, caseItem.case_number || caseItem.customer_name, e)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                data-testid={`delete-case-${caseItem._id}`}
                                title="Akte löschen"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              // Grid View
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                {cases.length === 0 ? (
                  <div className="col-span-full py-12 text-center text-gray-500">
                    <Camera className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">Keine Akten vorhanden</p>
                  </div>
                ) : (
                  cases.map((caseItem, idx) => (
                    <div 
                      key={caseItem._id || idx}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate(`/new-case?edit=${caseItem._id}`)}
                      data-testid={`case-card-${idx}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0 shadow-sm border border-gray-200">
                          {caseItem.photos && caseItem.photos.length > 0 ? (
                            <img 
                              src={getBackendUrl() + caseItem.photos[0]}
                              alt="Fall Vorschau" 
                              className="w-full h-full object-cover"
                              data-testid={`case-photo-grid-${caseItem._id}`}
                            />
                          ) : (
                            <Camera className="w-8 h-8 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate">{caseItem.customer_name || 'Unbekannt'}</h3>
                          <p className="text-sm text-gray-600">{caseItem.manufacturer || '-'} {caseItem.model || ''}</p>
                          <p className="text-xs text-gray-500 mt-1">{caseItem.license_plate || '-'}</p>
                          {/* Rechnungswerte */}
                          <div className="mt-2 flex items-center gap-2">
                            <span className="text-sm font-semibold text-blue-700">
                              {caseItem.invoice_total_brutto ? parseFloat(caseItem.invoice_total_brutto).toFixed(2) : '0.00'} € brutto
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {getWorkflowBadge(caseItem)}
                          {getStatusProgress(caseItem.workflow_status)}
                        </div>
                        <button
                          onClick={(e) => deleteCase(caseItem._id, caseItem.case_number || caseItem.customer_name, e)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          data-testid={`delete-case-grid-${caseItem._id}`}
                          title="Akte löschen"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
            
            {/* Pagination Controls */}
            {cases.length > 0 && (
              <div className="mt-6 bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="text-sm text-gray-600">
                    Zeige <span className="font-semibold text-gray-900">{indexOfFirstItem + 1}-{Math.min(indexOfLastItem, cases.length)}</span> von <span className="font-semibold text-gray-900">{cases.length}</span> Akten
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Pro Seite:</span>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                        data-testid="items-per-page-select"
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>

                    {totalPages > 1 && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          data-testid="prev-page-button"
                        >
                          ‹ Zurück
                        </button>
                        
                        <div className="flex items-center gap-1">
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }
                            
                            return (
                              <button
                                key={pageNum}
                                onClick={() => handlePageChange(pageNum)}
                                className={`min-w-[40px] h-10 rounded-lg text-sm font-medium transition-all ${
                                  currentPage === pageNum
                                    ? 'bg-blue-600 text-white shadow-md'
                                    : 'border border-gray-300 hover:bg-gray-50 text-gray-700'
                                }`}
                                data-testid={`page-${pageNum}-button`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                        </div>
                        
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          data-testid="next-page-button"
                        >
                          Weiter ›
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;
